import React, { useState } from "react";
import { FiMail, FiPhone, FiChevronDown } from "react-icons/fi";
import { SALES_PHONE } from "../config/brand";

/**
 * Ways to reach a real person.
 *
 * Only what actually exists: the placeholder phone number, the invented New
 * York address and the "live chat available 24/7" that was never built have
 * been taken out rather than left for a customer to try.
 */
const CONTACT_ITEMS = [
  {
    icon: FiPhone,
    title: "Sales",
    lines: [SALES_PHONE],
    href: `tel:${SALES_PHONE.replace(/[^0-9]/g, "")}`,
  },
  {
    icon: FiMail,
    title: "Email",
    lines: ["support@accountrix.com"],
    href: "mailto:support@accountrix.com",
  },
];

/**
 * Answers that match what the software actually does.
 *
 * The payment answers follow the plans as sold, and the security answer claims
 * only what is built — the two-factor sign-in and security audits it used to
 * promise were never there.
 */
const FAQS = [
  {
    q: "How does payment work?",
    a: "Each plan is billed once a year. You are charged when you sign up and again each year after that, and you can cancel at any time.",
  },
  {
    q: "Do you offer discounts?",
    a: "From time to time we run offers. If you have a discount code, enter it on the payment page and it comes off your first year.",
  },
  {
    q: "Can I change my plan later?",
    a: "Yes. Get in touch and we will move you to another plan, charging or refunding the difference between what you paid and the new plan.",
  },
  {
    q: "Is my data secure?",
    a: "Your data travels over an encrypted connection, each person is given access only to the companies they work on, and every change of substance is written to an audit log showing who made it and when.",
  },
];

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      {/* With no form to sit beside, the contact details carry the page on
          their own, so they are centred and spread across it rather than
          stranded in a half-width column. */}
      <section className="section-container py-16">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Get in Touch</h1>
          <p className="mt-4 text-slate-600">
            Have questions? We're here to help. Reach out to our team and we'll
            get back to you as soon as possible.
          </p>
        </div>

        {/* Two ways to get in touch, so they sit side by side rather than
            rattling around in a four-column grid built for four. */}
        <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2">
          {CONTACT_ITEMS.map(({ icon: Icon, title, lines, href }) => (
            <div
              key={title}
              className="card flex flex-col items-center p-7 text-center transition-shadow hover:shadow-md"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon size={20} />
              </span>
              <h3 className="mt-4 text-sm font-semibold">{title}</h3>
              {lines.map((line) => (
                <a
                  key={line}
                  href={href}
                  className="mt-1 text-base font-semibold text-navy transition-colors hover:text-primary"
                >
                  {line}
                </a>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-container pb-20">
        <h2 className="text-center text-2xl font-bold">
          Frequently Asked Questions
        </h2>
        <div className="mx-auto mt-8 max-w-3xl space-y-3">
          {FAQS.map((faq, i) => {
            const open = openFaq === i;
            return (
              <div key={faq.q} className="card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : i)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="text-sm font-semibold text-navy">
                    {faq.q}
                  </span>
                  <FiChevronDown
                    className={`shrink-0 text-slate-400 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                    size={18}
                  />
                </button>
                {open && (
                  <p className="border-t border-line px-5 py-4 text-sm leading-relaxed text-slate-600">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
